import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Row, 
  Col, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  message,
  Table,
  Tooltip
} from 'antd';
import { 
  MailOutlined, 
  MessageOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { reviewRequestService } from '../../../services/reviewService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ReviewRequestAutomation = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const requests = await reviewRequestService.getPendingReviewRequests();
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      message.error('Failed to load review requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReviewRequest = async (requestId, method = 'email') => {
    try {
      await reviewRequestService.markReviewRequestSent(requestId, method);
      message.success(`Review request sent via ${method}`);
      fetchPendingRequests();
    } catch (error) {
      console.error('Error sending review request:', error);
      message.error('Failed to send review request');
    }
  };

  const handleCreateReviewRequest = async (values) => {
    try {
      const reviewRequest = {
        userId: values.userId,
        serviceId: values.serviceId,
        serviceType: values.serviceType,
        serviceDate: values.serviceDate.toISOString(),
        crewLeader: values.crewLeader,
        customerEmail: values.customerEmail,
        customerPhone: values.customerPhone
      };

      await reviewRequestService.createReviewRequest(reviewRequest);
      message.success('Review request created successfully');
      setModalVisible(false);
      form.resetFields();
      fetchPendingRequests();
    } catch (error) {
      console.error('Error creating review request:', error);
      message.error('Failed to create review request');
    }
  };

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (text, record) => (
        <div>
          <Text strong>{text || 'Unknown Customer'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.customerEmail}
          </Text>
        </div>
      )
    },
    {
      title: 'Service',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (serviceType) => (
        <Tag color="blue">{serviceType}</Tag>
      )
    },
    {
      title: 'Service Date',
      dataIndex: 'serviceDate',
      key: 'serviceDate',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'Crew Leader',
      dataIndex: 'crewLeader',
      key: 'crewLeader'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Pending' },
          sent: { color: 'blue', icon: <SendOutlined />, text: 'Sent' },
          completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Completed' },
          expired: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Expired' }
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Send Email Request">
            <Button 
              type="primary" 
              size="small" 
              icon={<MailOutlined />}
              onClick={() => handleSendReviewRequest(record.id, 'email')}
              disabled={record.status !== 'pending'}
            >
              Email
            </Button>
          </Tooltip>
          <Tooltip title="Send SMS Request">
            <Button 
              size="small" 
              icon={<MessageOutlined />}
              onClick={() => handleSendReviewRequest(record.id, 'sms')}
              disabled={record.status !== 'pending'}
            >
              SMS
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Review Request Automation
            </Title>
            <Text type="secondary">
              Manage automated review requests for completed services
            </Text>
          </div>
          <Button 
            type="primary" 
            onClick={() => setModalVisible(true)}
          >
            Create Review Request
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={pendingRequests}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create Review Request Modal */}
      <Modal
        title="Create Review Request"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateReviewRequest}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="userId"
                label="User ID"
                rules={[{ required: true, message: 'Please enter user ID' }]}
              >
                <Input placeholder="User ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="serviceId"
                label="Service ID"
                rules={[{ required: true, message: 'Please enter service ID' }]}
              >
                <Input placeholder="Service ID" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="serviceType"
            label="Service Type"
            rules={[{ required: true, message: 'Please select service type' }]}
          >
            <Select placeholder="Select service type">
              <Option value="lawn-care">Lawn Care</Option>
              <Option value="painting">Painting</Option>
              <Option value="cleaning">Cleaning</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="workshop">Workshop</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="serviceDate"
                label="Service Date"
                rules={[{ required: true, message: 'Please select service date' }]}
              >
                <DatePicker style={{ width: '100%' }} suffixIcon={<CalendarOutlined />} getPopupContainer={(trigger) => trigger.parentElement} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="crewLeader"
                label="Crew Leader"
                rules={[{ required: true, message: 'Please enter crew leader name' }]}
              >
                <Input placeholder="Crew leader name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customerEmail"
                label="Customer Email"
                rules={[
                  { required: true, message: 'Please enter customer email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="customer@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customerPhone"
                label="Customer Phone"
                rules={[{ required: true, message: 'Please enter customer phone' }]}
              >
                <Input placeholder="+1 (555) 123-4567" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Review Request
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ReviewRequestAutomation;
